import { InfoCard } from '@/components/info-card';
import { Button } from '@/components/ui/button';
import { getCoachVendor, getMeCoach } from '@/lib/api-calls';
import { useQuery } from '@tanstack/react-query';
import CreditCardIcon from '@/components/assets/credit-card-icon.svg?react';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { CoachPaymentNotSynced, CoachPaymentSyncForm } from './coach-payment-not-synced';
import { useState } from 'react';
import { InfoKeyValue } from '@/components/info-key-value';
import {
  accountClassToLabel,
  getCoachACHPaymentOrNull,
  getCoachPaymentInternationalOrNull,
  paymentAccountType,
  paymentAccountTypeKind,
} from '@airreading/common/data';
import { cn } from '@/lib/tailwind-utils';

import { PaymentAlert } from './payment-alert';
import { maskedValueClassName } from '@/lib/masked-value';

export const CoachPaymentForm = () => {
  const { data: profile } = useQuery(getMeCoach());
  const isPaymentConnected = !!profile?.paymentInstrumentId;
  const isVendorConnected = !!profile?.vendorId;
  const isInternationalAccount = profile?.paymentAccountType === paymentAccountType.enum.international_wire;

  const { data: vendor } = useQuery({ ...getCoachVendor(), enabled: !!isVendorConnected });

  const [isUpdateAccountFormOpen, setIsUpdateAccountFormOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const achPayment = getCoachACHPaymentOrNull(vendor);
  const internationalPayment = getCoachPaymentInternationalOrNull(vendor);

  return (
    <>
      <InfoCard
        headerText={'Direct deposit info'}
        headerActions={
          isVendorConnected && (
            <Button
              variant={'outline'}
              onClick={() => setIsUpdateAccountFormOpen(true)}
              data-testid="profile-payment-form-button-update"
            >
              Update Direct Deposit Info
            </Button>
          )
        }
      >
        {isVendorConnected ? (
          <section>
            <div className="mb-4">
              <PaymentAlert {...profile} />
            </div>

            {isInternationalAccount ? (
              <>
                <div className="grid grid-cols-4 gap-8">
                  <InfoKeyValue label="Account holder name" value={vendor?.company_name} />
                  <InfoKeyValue
                    label="Swift / BIC code"
                    labelClassName={maskedValueClassName}
                    value={internationalPayment?.payment_accounts.details.masked_swift_code}
                  />
                  <InfoKeyValue
                    label="IBAN / Account Number"
                    labelClassName={maskedValueClassName}
                    value={internationalPayment?.payment_accounts.details.masked_iban}
                  />
                  <InfoKeyValue
                    label="Country"
                    value={internationalPayment?.payment_accounts.details.address.country}
                  />
                  <InfoKeyValue
                    label="Status"
                    value={
                      <div
                        className={cn('text-orange-600 flex items-center gap-2', {
                          'text-lime-600': isPaymentConnected,
                        })}
                      >
                        <span className="w-2 h-2 rounded-full bg-current block" />
                        <span>
                          {isPaymentConnected ? 'Direct Deposit setup ready' : 'Direct Deposit not set up yet'}
                        </span>
                      </div>
                    }
                  />
                  <InfoKeyValue label="City" value={internationalPayment?.payment_accounts.details.address.city} />
                  <InfoKeyValue
                    label="Address"
                    value={
                      (internationalPayment?.payment_accounts.details.address.line1 ?? '') +
                      ' ' +
                      (internationalPayment?.payment_accounts.details.address.line2 ?? '')
                    }
                  />
                  <InfoKeyValue
                    label="ZIP code"
                    value={internationalPayment?.payment_accounts.details.address.postal_code}
                  />
                </div>
                {isUpdateAccountFormOpen && (
                  <CoachPaymentSyncForm
                    type="update"
                    defaultValues={{
                      companyName: internationalPayment?.company_name,
                    }}
                    onClose={() => setIsUpdateAccountFormOpen(false)}
                  />
                )}
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-8">
                  <InfoKeyValue label="Account holder name" value={vendor?.company_name} />
                  <InfoKeyValue
                    label="Account type"
                    value={
                      achPayment?.payment_accounts.details.type === paymentAccountTypeKind.enum.ACH
                        ? achPayment?.payment_accounts.details.account_type
                        : ''
                    }
                  />
                  <InfoKeyValue
                    label="Routing number"
                    labelClassName={maskedValueClassName}
                    value={achPayment?.payment_accounts.details.masked_routing_number}
                  />
                  <InfoKeyValue
                    label="Status"
                    value={
                      <div
                        className={cn('text-orange-600 flex items-center gap-2', {
                          'text-lime-600': isPaymentConnected,
                        })}
                      >
                        <span className="w-2 h-2 rounded-full bg-current block" />
                        <span>
                          {isPaymentConnected ? 'Direct Deposit setup ready' : 'Bank account info missing or invalid'}
                        </span>
                      </div>
                    }
                  />
                  <InfoKeyValue
                    label="Account class"
                    value={
                      achPayment?.payment_accounts.details.type === paymentAccountTypeKind.enum.ACH
                        ? achPayment?.payment_accounts.details.account_class &&
                          accountClassToLabel[achPayment?.payment_accounts.details.account_class]
                        : ''
                    }
                  />
                  <InfoKeyValue
                    label="Account number"
                    className="not-italic normal-case tracking-normal"
                    labelClassName={maskedValueClassName}
                    value={achPayment?.payment_accounts.details.masked_account_number}
                  />
                </div>
                {isUpdateAccountFormOpen && (
                  <CoachPaymentSyncForm
                    type="update"
                    onClose={() => setIsUpdateAccountFormOpen(false)}
                    defaultValues={{
                      type: achPayment?.payment_accounts.details.type,
                      companyName: vendor?.company_name,
                      accountClass: achPayment?.payment_accounts.details.account_class ?? undefined,
                      accountType: achPayment?.payment_accounts.details.account_type ?? undefined,
                    }}
                    currentAccount={{
                      routingNumber: achPayment?.payment_accounts.details.masked_routing_number,
                      accountNumber: achPayment?.payment_accounts.details.masked_account_number,
                    }}
                  />
                )}
              </>
            )}
          </section>
        ) : (
          <section className="grid place-items-center">
            <CreditCardIcon className="mb-4" />
            <p className="text-slate-400 mb-2 font-normal text-lg">Direct deposit not ready yet</p>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button variant={'outline'} className="font-bold" data-testid="profile-payment-form-button-add">
                  Add direct deposit info
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="profile-payment-modal-add">
                <CoachPaymentNotSynced
                  onClose={() => setIsOpen(false)}
                  onOpen={() => setIsUpdateAccountFormOpen(true)}
                />
              </DialogContent>
            </Dialog>
          </section>
        )}
      </InfoCard>
      <div className="pb-4">
        <p className="text-[#606060]">
          International Bank Account Notice: Starting January 1st, 2026, we no longer support adding or changing
          international bank accounts. To receive payments internationally, please set up a Wise account at{' '}
          <a href="wise.com" className="text-blue-500">
            wise.com
          </a>
          , then enter your Wise US routing and account numbers here as an ACH payment method.With Wise, you can convert
          your USD payments to your local currency and withdraw to your local bank—typically at better rates and lower
          fees than traditional international wires.
        </p>
      </div>
    </>
  );
};
